import { useMemo } from 'react';
import useSWR from 'swr';

import apiRequest from '@/services/apiRequest';
import type { IBid } from '@/services/types/bids/bids';
import { getCoreApi } from '@/utility/getCoreApi';

const VISIBLE_MATCH_STATUSES = new Set(['open', 'pending']);
const ONE_WAY_REQUEST_SOURCE = 'one-way-request';

const filterAndSortMatches = (bid: IBid): IBid => ({
  ...bid,
  bid_matches: (bid.bid_matches?.filter(m => VISIBLE_MATCH_STATUSES.has(m.status)) || []).sort(
    (a, b) => {
      // Pending matches first, then open
      if (a.status !== b.status) {
        if (a.status === 'pending') return -1;
        if (b.status === 'pending') return 1;
      }
      // Within same status, sort by score descending
      return (b.score ?? 0) - (a.score ?? 0);
    },
  ),
});

export const useBids = (shouldFetch: boolean) => {
  const fetcher = async () => {
    return apiRequest<IBid[]>(
      {
        url: `${getCoreApi()}/bids`,
        method: 'GET',
      },
      true,
    );
  };

  const { data, error, isLoading, mutate } = useSWR<IBid[]>(shouldFetch ? 'bids' : null, fetcher, {
    revalidateOnFocus: false,
  });

  const activeBids = useMemo(() => {
    if (!data?.length) return [];
    return data.filter(bid => bid.source !== ONE_WAY_REQUEST_SOURCE).map(filterAndSortMatches);
  }, [data]);

  return {
    activeBids,
    isLoading,
    error,
    revalidate: mutate,
  };
};
